import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Clock3, Coffee, LogIn, LogOut, RotateCcw, Undo2 } from "lucide-react";

const OPEN = ["working", "on_break"];
const STATUS = {
  not_logged_in: ["Not logged in", "bg-slate-500/15 text-slate-600 dark:text-slate-300"],
  working: ["Working", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"],
  on_break: ["On break", "bg-amber-500/15 text-amber-700 dark:text-amber-300"],
  clocked_out: ["Logged out", "bg-[#255058]/15 text-[#255058] dark:text-teal-200"],
};
let greetingCache = null;

export function hhmm(total) {
  const value = Math.max(0, Math.floor(total || 0));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

export function shiftTime(iso, tz, h12 = false) {
  if (!iso) return "—";
  const opts = { hour: h12 ? "numeric" : "2-digit", minute: "2-digit", hour12: h12 };
  try {
    return new Date(iso).toLocaleTimeString(h12 ? "en-US" : "en-GB", { ...opts, timeZone: tz || "UTC" });
  } catch {
    return new Date(iso).toLocaleTimeString([], opts);
  }
}

function overlapSeconds(a0, a1, b0, b1) {
  return Math.max(0, (Math.min(a1, b1) - Math.max(a0, b0)) / 1000);
}

function useLiveSummary(payload) {
  const [now, setNow] = useState(Date.now());
  const fetchedAt = useRef(Date.now());
  useEffect(() => {
    fetchedAt.current = Date.now();
    setNow(Date.now());
  }, [payload]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const s = payload?.summary;
  if (!s) return null;
  const start = new Date(s.shift_start).getTime();
  const end = new Date(s.shift_end).getTime();
  const out = { ...s, worked_seconds: (s.regular_seconds ?? s.regular_minutes * 60) + s.overtime_minutes * 60 };
  out.break_seconds = s.break_seconds;
  if (payload.status === "working") out.worked_seconds += overlapSeconds(fetchedAt.current, now, start, end);
  if (payload.status === "on_break") out.break_seconds = s.break_seconds + (now - fetchedAt.current) / 1000;
  out.break_minutes = out.break_seconds / 60;
  out.break_remaining_seconds = Math.max(0, s.break_allowance_minutes * 60 - out.break_seconds);
  out.shift_ended = s.shift_ended || now >= end;
  out.shift_started = s.shift_started || now >= start;
  out.shift_progress = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  return out;
}

function useBreakWarnings(payload, live) {
  const warned = useRef({});
  useEffect(() => {
    if (!live || payload?.status !== "on_break") return;
    const key = payload.work_date;
    const limit = live.break_allowance_minutes;
    if (live.break_minutes >= limit - 5 && live.break_minutes < limit && !warned.current[`${key}-5`]) {
      warned.current[`${key}-5`] = true;
      toast.warning("5 minutes of break time left for this shift.");
      if (window.Notification?.permission === "granted") new Notification("Break ending soon", { body: "5 minutes of break time left.", tag: `break5-${key}` });
    }
    if (live.break_minutes >= limit && !warned.current[`${key}-grace`]) {
      warned.current[`${key}-grace`] = true;
      toast.error(`Break allowance used — ${live.break_grace_minutes} min grace has started.`);
    }
  }, [payload, live]);
}

async function greet(action, data, name) {
  if (!greetingCache) {
    greetingCache = await api.get("/hrms/settings/greetings").then((r) => r.data.library).catch(() => ({}));
  }
  const lines = greetingCache[action] || [];
  if (!lines.length || !data?.summary) return;
  const s = data.summary;
  const hour = new Date().getHours();
  const worked = s.working_minutes || 0;
  const values = {
    name: (name || "there").split(" ")[0],
    daypart: hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening",
    shift: data.shift?.name || "shift",
    end: `${shiftTime(s.shift_end, data.shift?.timezone, true)} ${data.shift?.timezone_label || ""}`.trim(),
    left: s.break_remaining_minutes,
    used: s.break_minutes,
    remaining: s.break_remaining_minutes,
    worked: `${Math.floor(worked / 60)}h ${worked % 60}m`,
    break: s.break_minutes,
  };
  const template = lines[Math.floor(Math.random() * lines.length)];
  toast.success(template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? ""), { duration: 4000 });
}

function Bar({ value, tone, testId }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted" data-testid={testId}>
      <div className={`h-full rounded-full transition-[width] duration-700 ${tone}`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

function Timer({ label, value, testId, tone }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3" data-testid={testId}>
      <div className={`font-mono text-xl font-bold tabular-nums sm:text-2xl ${tone || ""}`}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ReasonDialog({ open, title, description, children, onClose, onSubmit, saving, submitLabel, testId }) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid={`${testId}-submit`}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StateCard({ testId, tone, title, body }) {
  return (
    <Card className={`border-l-4 ${tone} p-5`} data-testid={testId}>
      <div className="font-display text-lg font-semibold">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{body}</div>
    </Card>
  );
}

function PrimaryButton({ status, live, saving, onAction }) {
  const config = {
    not_logged_in: ["login", "Login", LogIn],
    working: ["break", "Start break", Coffee],
    on_break: ["back", "Back from break", Undo2],
    clocked_out: ["resume", "Resume shift", RotateCcw],
  }[status];
  if (!config) return null;
  const [action, label, Icon] = config;
  const disabled = saving || (action === "break" && !live.can_start_break);
  return (
    <Button
      size="lg"
      onClick={() => onAction(action)}
      disabled={disabled}
      className="h-12 min-w-[200px] rounded-full bg-[#255058] px-8 text-base text-white shadow-md transition-transform hover:-translate-y-0.5 hover:bg-[#1c3d43]"
      data-testid={`hrms-attendance-${action}-button`}
    >
      <Icon className="mr-2 h-5 w-5" /> {label}
    </Button>
  );
}

export function AttendanceCard({ attendance, saving, onAction, onChanged }) {
  const { user } = useAuth();
  const live = useLiveSummary(attendance);
  useBreakWarnings(attendance, live);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [earlyOpen, setEarlyOpen] = useState(false);
  const [otOpen, setOtOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState("1");
  const [busy, setBusy] = useState(false);

  if (!attendance) return <Card className="p-5" data-testid="hrms-attendance-loading">Loading attendance…</Card>;
  if (attendance.exempt)
    return <StateCard testId="hrms-attendance-exempt" tone="border-l-[#255058]" title="Attendance exempt" body="Founder and Super Admin accounts do not clock in and are excluded from attendance, late and break counts." />;
  if (attendance.status === "shift_unassigned")
    return <StateCard testId="hrms-shift-unassigned" tone="border-l-amber-500" title="Shift assignment needed" body="HR must assign an active shift before you can start attendance." />;
  if (attendance.status === "attendance_disabled")
    return <StateCard testId="hrms-attendance-disabled" tone="border-l-slate-400" title="Attendance disabled" body="An administrator has turned off attendance tracking for your account." />;

  const shift = attendance.shift || {};
  const tz = shift.timezone;
  const tzLabel = shift.timezone_label || "UTC";
  const status = attendance.status;
  const [statusLabel, statusTone] = STATUS[status] || [status, ""];
  const needsApproval = !shift.is_default && !live.shift_ended && !attendance.early_logout?.approved;
  const breakPct = live.break_allowance_minutes ? (live.break_minutes / live.break_allowance_minutes) * 100 : 0;

  const run = async (action) => {
    const data = await onAction(action);
    if (data) greet(action, data, user?.name);
  };
  const onLogout = () => {
    if (!needsApproval) return setConfirmOpen(true);
    if (attendance.early_logout?.pending) return toast.info("Your early logout request is awaiting approval.");
    setReason("");
    setEarlyOpen(true);
  };
  const submit = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      setEarlyOpen(false);
      setOtOpen(false);
      onChanged();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden border-l-4 border-l-[#255058] p-5 sm:p-6" data-testid="hrms-attendance-widget">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Shift day {attendance.work_date}</div>
          <div className="mt-1 font-display text-lg font-semibold sm:text-xl" data-testid="attendance-shift-label">
            {shift.name || "Shift"} · {shiftTime(live.shift_start, tz, true)} – {shiftTime(live.shift_end, tz, true)}, {tzLabel}
          </div>
          {live.first_in_at && (
            <div className="mt-1 text-xs text-muted-foreground" data-testid="attendance-logged-in-at">
              Logged in at {shiftTime(live.first_in_at, tz, true)} {tzLabel}
            </div>
          )}
        </div>
        <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${statusTone}`} data-testid="attendance-status-badge">
          {statusLabel}
        </span>
      </div>

      {attendance.previous_shift_open && (
        <div className="mt-3 text-sm text-amber-600" data-testid="attendance-previous-open">Your {attendance.work_date} shift is still clocked in. Log out to close it.</div>
      )}
      {OPEN.includes(status) && !live.shift_started && (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Clock3 className="h-3.5 w-3.5" /> Worked time starts counting at {shiftTime(live.shift_start, tz, true)} {tzLabel}.</div>
      )}

      {status === "on_break" && (
        <div className="mt-4 rounded-lg bg-amber-500/10 p-4 text-center" data-testid="attendance-break-countdown">
          <div className="text-xs text-muted-foreground">Break time left</div>
          <div className={`font-mono text-3xl font-bold tabular-nums ${live.break_remaining_seconds <= 0 ? "text-red-600" : "text-amber-700 dark:text-amber-300"}`}>
            {clock(live.break_remaining_seconds)}
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Timer label="Worked" value={clock(live.worked_seconds)} testId="attendance-worked" />
        <Timer label="Break taken" value={clock(live.break_seconds)} testId="attendance-break-taken" tone={live.break_over_limit ? "text-red-600" : ""} />
        <Timer label="Break remaining" value={clock(live.break_remaining_seconds)} testId="attendance-break-remaining" />
        <Timer label="Overtime (approved)" value={hhmm(live.overtime_minutes)} testId="attendance-overtime" />
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 flex justify-between text-xs text-muted-foreground">
            <span>Shift completion</span><span>{Math.round(live.shift_progress)}%</span>
          </div>
          <Bar value={live.shift_progress} tone="bg-[#255058]" testId="attendance-shift-progress" />
        </div>
        <div>
          <div className="mb-1.5 flex justify-between text-xs text-muted-foreground" data-testid="attendance-breaks-used">
            <span>{live.break_count} of {live.breaks_allowed} breaks used, {Math.ceil(live.break_remaining_seconds / 60)} min left</span>
            {live.is_late && <span className="text-amber-600" data-testid="attendance-late">Late {live.late_minutes} min</span>}
          </div>
          <Bar value={breakPct} tone={breakPct >= 100 ? "bg-red-500" : "bg-amber-500"} testId="attendance-break-progress" />
        </div>
      </div>

      {live.shift_ended && OPEN.includes(status) && !shift.is_default && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm" data-testid="attendance-shift-ended-banner">
          <span className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Shift ended. Clock out or request overtime.
            {attendance.overtime?.pending && " Overtime request pending."}
          </span>
          <Button size="sm" variant="outline" onClick={() => { setReason(""); setOtOpen(true); }} disabled={attendance.overtime?.pending} data-testid="attendance-request-overtime-button">
            Request overtime
          </Button>
        </div>
      )}

      <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
        <PrimaryButton status={status} live={live} saving={saving} onAction={run} />
        {OPEN.includes(status) && (
          <Button variant="ghost" onClick={onLogout} disabled={saving} className="text-muted-foreground hover:text-foreground" data-testid="hrms-attendance-logout-button">
            <LogOut className="mr-1.5 h-4 w-4" /> Logout (end work)
          </Button>
        )}
      </div>
      {status === "working" && live.break_block_reason && (
        <div className="mt-2 text-center text-xs text-muted-foreground sm:text-left" data-testid="attendance-break-blocked">{live.break_block_reason}</div>
      )}
      {attendance.early_logout?.pending && <div className="mt-2 text-xs text-muted-foreground" data-testid="attendance-early-pending">Early logout request pending approval.</div>}
      {attendance.early_logout?.approved && !live.shift_ended && <div className="mt-2 text-xs text-emerald-600" data-testid="attendance-early-approved">Early logout approved — you can log out.</div>}
      {live.gaps?.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground" data-testid="attendance-gaps">
          {live.gaps.map((gap) => (
            <div key={gap.from}>Logged out {shiftTime(gap.from, tz)} to {shiftTime(gap.to, tz)}</div>
          ))}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="attendance-clockout-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Are you sure you want to clock out?</DialogTitle>
            <DialogDescription>
              {status === "on_break" ? "Your current break will end now. " : ""}
              You can resume later in this shift — worked and break time continue on the same record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="attendance-clockout-cancel">Cancel</Button>
            <Button onClick={() => { setConfirmOpen(false); run("logout"); }} className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="attendance-clockout-confirm">Clock out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReasonDialog
        open={earlyOpen}
        testId="attendance-early-logout-dialog"
        title="Early logout request"
        description={`Your shift ends at ${shiftTime(live.shift_end, tz, true)} ${tzLabel}. Your Team Lead must approve before you can log out early.`}
        saving={busy}
        submitLabel="Send request"
        onClose={() => setEarlyOpen(false)}
        onSubmit={() => submit(() => api.post("/hrms/me/early-logout", { reason }), "Early logout request sent")}
      >
        <Label>Reason</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="attendance-early-logout-reason" />
      </ReasonDialog>
      <ReasonDialog
        open={otOpen}
        testId="attendance-overtime-dialog"
        title="Request overtime"
        description="Time after shift end counts only once overtime is approved, on this same shift day."
        saving={busy}
        submitLabel="Send request"
        onClose={() => setOtOpen(false)}
        onSubmit={() => submit(() => api.post("/hrms/overtime/requests", { work_date: attendance.work_date, hours: Number(hours), reason }), "Overtime request sent")}
      >
        <Label>Hours</Label>
        <Input type="number" min="0.25" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="attendance-overtime-hours" />
        <Label>Reason</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="attendance-overtime-reason" />
      </ReasonDialog>
    </Card>
  );
}
